import { useEffect, useRef } from 'react';
import { API_ENDPOINTS, fetchWithRetry, getApiUrl } from '../config/api';
import useStore from '../store/useStore';
import {
  extractGameTokenFromLoginResponse,
  extractCrossDayTimePatch,
  extractServerNowFromLoginResponse,
  extractTodayEndFromLoginResponse
} from '../utils/authResponse';

/** 游戏内跳转登录会话标记：用于控制“刷新后退出登录”行为 */
const GAME_AUTH_SESSION_KEY = 'ws_game_auth_session';
const GAME_AUTH_PARAM_KEYS = [
  'ws_auth',
  'fpid',
  'playerid',
  'token',
  'gametoken',
  'EndOfDay',
  'endOfDay',
  'endofday',
  'today_end',
  'todayEnd',
  'Now',
  'now',
  'now_s',
];

/**
 * 获取游戏透传参数：
 * - 先读标准 query（window.location.search）
 * - 若未命中，再兼容 hash 场景（#/path?ws_auth=xxx）
 */
const getGameAuthParamsFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  if (GAME_AUTH_PARAM_KEYS.some((key) => searchParams.has(key))) {
    return searchParams;
  }

  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex > -1) {
    const hashQuery = hash.substring(queryIndex + 1);
    const hashParams = new URLSearchParams(hashQuery);
    if (GAME_AUTH_PARAM_KEYS.some((key) => hashParams.has(key))) {
      return hashParams;
    }
  }

  return searchParams;
};

/**
 * base64url 解码：
 * - 兼容 URL 安全字符（- _）
 * - 自动补齐 '='
 * - 输出 UTF-8 字符串
 */
const decodeBase64Url = (value) => {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    const padded = normalized + (padding ? '='.repeat(4 - padding) : '');
    const binary = atob(padded);
    const percentEncoded = Array.from(binary)
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('');
    return decodeURIComponent(percentEncoded);
  } catch (error) {
    console.error('[AuthChecker] decode ws_auth failed:', error);
    return '';
  }
};

/**
 * 解析游戏透传登录参数：
 * 1. 优先 ws_auth（推荐）
 * 2. 兼容明文字段（fpid/playerid/token）
 */
const parseGameAuthUserFromUrl = () => {
  const params = getGameAuthParamsFromUrl();

  // 方案1：ws_auth（base64url JSON）
  const wsAuth = params.get('ws_auth');
  if (wsAuth) {
    try {
      const json = decodeBase64Url(wsAuth);
      if (json) {
        const payload = JSON.parse(json);
        const fpid = payload?.fpid;
        const playerid = Number(payload?.playerid);
        // 固定读取透传 token 字段，并同步写入 gameToken（后续所有业务请求统一使用 gameToken）
        const token = payload?.token;
        const timePatch = extractCrossDayTimePatch(payload);
        if (fpid && !Number.isNaN(playerid) && token) {
          return {
            id: String(fpid),
            playerid: playerid,
            token: String(token),
            gameToken: String(token),
            provider: payload?.provider || 'game',
            name: payload?.name || `Player ${playerid}`,
            Avatar: payload?.avatar || '',
            Coin: Number(payload?.coin || 0),
            Level: Number(payload?.level || 0),
            VipLevel: Number(payload?.vipLevel || 0),
            Gem: Number(payload?.gem || 0),
            ...timePatch,
          };
        }
      }
    } catch (error) {
      console.error('[AuthChecker] parse ws_auth failed:', error);
    }
  }

  // 方案2：明文字段兜底（兼容历史链接）
  const fpid = params.get('fpid');
  const playerid = Number(params.get('playerid'));
  // 固定读取 token 参数，不再做 gametoken 兜底
  const token = params.get('token');
  const queryTimePatch = extractCrossDayTimePatch({
    EndOfDay: params.get('EndOfDay'),
    endofday: params.get('endofday'),
    endOfDay: params.get('endOfDay'),
    today_end: params.get('today_end'),
    todayEnd: params.get('todayEnd'),
    Now: params.get('Now'),
    now: params.get('now'),
    now_s: params.get('now_s'),
  });
  if (fpid && !Number.isNaN(playerid) && token) {
    return {
      id: String(fpid),
      playerid: playerid,
      token: String(token),
      gameToken: String(token),
      provider: 'game',
      name: `Player ${playerid}`,
      Avatar: '',
      Coin: 0,
      Level: 0,
      VipLevel: 0,
      Gem: 0,
      ...queryTimePatch,
    };
  }

  return null;
};

/**
 * 清理 URL 中的透传登录参数，减少 token 暴露时间
 */
const clearGameAuthParamsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  GAME_AUTH_PARAM_KEYS.forEach((key) => params.delete(key));

  const nextQuery = params.toString();
  let nextHash = window.location.hash || '';
  const hashQueryIndex = nextHash.indexOf('?');
  if (hashQueryIndex > -1) {
    const hashPath = nextHash.substring(0, hashQueryIndex);
    const hashQuery = nextHash.substring(hashQueryIndex + 1);
    const hashParams = new URLSearchParams(hashQuery);
    GAME_AUTH_PARAM_KEYS.forEach((key) => hashParams.delete(key));
    const cleanedHashQuery = hashParams.toString();
    nextHash = `${hashPath}${cleanedHashQuery ? `?${cleanedHashQuery}` : ''}`;
  }

  const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${nextHash}`;
  window.history.replaceState({}, '', nextUrl);
};

/** 从对象中按候选 key 顺序取第一个有值字段 */
const pickFirstDefined = (source, keys) => {
  if (!source || typeof source !== 'object') return undefined;
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

/** 安全转 number，失败时返回 null */
const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** 判断一个对象是否看起来像“玩家信息对象” */
const isPlayerLikeObject = (item) => {
  if (!item || typeof item !== 'object') return false;
  return [
    'Name', 'name',
    'Avatar', 'avatar',
    'Coin', 'coin',
    'Gem', 'gem',
    'Level', 'level',
    'VipLevel', 'vipLevel', 'viplevel', 'Viplevel',
    'userinfo', 'Userinfo'
  ].some((key) => item[key] !== undefined && item[key] !== null);
};

/**
 * 深度遍历响应，找第一个“玩家对象”：
 * 兼容后端字段层级变化，避免固定路径失效。
 */
const findFirstPlayerLikeObject = (root) => {
  if (!root || typeof root !== 'object') return null;
  const queue = [root];
  let visited = 0;

  while (queue.length > 0 && visited < 200) {
    const current = queue.shift();
    visited += 1;
    if (!current || typeof current !== 'object') continue;

    if (isPlayerLikeObject(current)) {
      return current;
    }

    if (Array.isArray(current)) {
      for (const item of current) queue.push(item);
      continue;
    }

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return null;
};

/**
 * 从后端响应中提取“玩家展示信息”：
 * 兼容多种字段命名，尽量容错，不依赖单一结构。
 */
const extractUserDisplayPatch = (data) => {
  if (!data || typeof data !== 'object') return null;

  const candidates = [
    data?.Player,
    data?.player,
    data?.data?.Player,
    data?.data?.player,
    data?.Data?.Player,
    data?.Data?.player,
    data?.result?.Player,
    data?.result?.player,
    findFirstPlayerLikeObject(data),
    data
  ];

  const playerLike = candidates.find((item) => {
    if (!item || typeof item !== 'object') return false;
    return [
      'Name', 'name',
      'Avatar', 'avatar',
      'Coin', 'coin',
      'Gem', 'gem',
      'Level', 'level',
      'VipLevel', 'vipLevel', 'viplevel', 'Viplevel'
    ].some((key) => item[key] !== undefined && item[key] !== null);
  });

  if (!playerLike) return null;

  const patch = {};

  const name = pickFirstDefined(playerLike, ['Name', 'name', 'Nickname', 'nickName']);
  const avatar = pickFirstDefined(playerLike, ['Avatar', 'avatar', 'Photo', 'photo']);
  const coin = toNumberOrNull(pickFirstDefined(playerLike, ['Coin', 'coin']));
  const gem = toNumberOrNull(pickFirstDefined(playerLike, ['Gem', 'gem']));
  const level = toNumberOrNull(pickFirstDefined(playerLike, ['Level', 'level']));
  const vipLevel = toNumberOrNull(pickFirstDefined(playerLike, ['VipLevel', 'vipLevel', 'viplevel', 'Viplevel']));
  const userinfoRaw = pickFirstDefined(playerLike, ['userinfo', 'Userinfo', 'userInfo', 'UserInfo']);

  let userinfo = null;
  if (typeof userinfoRaw === 'string') {
    try {
      userinfo = JSON.parse(userinfoRaw);
    } catch {
      userinfo = null;
    }
  } else if (userinfoRaw && typeof userinfoRaw === 'object') {
    userinfo = userinfoRaw;
  }

  const fpid = pickFirstDefined(data, ['Fpid', 'fpid', 'FPID', 'A10pid', 'a10pid']);
  const playerId = toNumberOrNull(
    pickFirstDefined(data, ['PlayerID', 'playerid', 'playerId']) ??
    pickFirstDefined(playerLike, ['ID', 'id', 'PlayerID', 'playerid', 'playerId'])
  );

  const nameFromUserInfo = pickFirstDefined(userinfo, ['name', 'Name', 'nickname', 'nickName']);
  const avatarFromUserInfo = pickFirstDefined(userinfo, ['protrait', 'portrait', 'avatar', 'Avatar', 'photo', 'Photo']);

  if (typeof nameFromUserInfo === 'string' && nameFromUserInfo.trim()) {
    patch.name = nameFromUserInfo.trim();
  } else if (typeof name === 'string' && name.trim()) {
    patch.name = name.trim();
  }

  if (typeof avatarFromUserInfo === 'string') {
    patch.Avatar = avatarFromUserInfo;
  } else if (typeof avatar === 'string') {
    patch.Avatar = avatar;
  }
  if (coin !== null) patch.Coin = coin;
  if (gem !== null) patch.Gem = gem;
  if (level !== null) patch.Level = level;
  if (vipLevel !== null) patch.VipLevel = vipLevel;
  if (fpid !== undefined && fpid !== null && String(fpid).trim()) patch.id = String(fpid);
  if (playerId !== null) patch.playerid = playerId;

  return Object.keys(patch).length > 0 ? patch : null;
};

const AuthChecker = () => {
  const { setUser, setLoginModalOpen, user, isAuthenticated, setAuthCheckDone } = useStore();
  const hasCheckedRef = useRef(false);

  // 密码哈希函数
  const hashPassword = async (password) => {
    try {
      // 检查是否在安全环境中（HTTPS 或 localhost）
      if (window.crypto && window.crypto.subtle && (window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      } else {
        // 非安全环境使用备用哈希
        console.warn('Web Crypto API not available, using fallback hash');
        return simpleHash(password);
      }
    } catch (error) {
      console.error('Hash error:', error);
      return simpleHash(password);
    }
  };

  // 简单的哈希函数（仅用于开发环境）
  const simpleHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  };

  // 邮箱自动登录
  const checkEmailAutoLogin = async () => {
    const savedCredentials = localStorage.getItem('emailCredentials');
    if (!savedCredentials) {
      return false;
    }

    try {
      const { email, password } = JSON.parse(savedCredentials);

      const hashPasswordValue = await hashPassword(password);
      const formData = new FormData();
      formData.append('Username', email);
      formData.append('Password', hashPasswordValue);
      formData.append('LoginOrigin', '6');
      formData.append('TokenOption', '1');
      

      const email_response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.REGISTER_ACCOUNT), {
        method: 'POST',
        headers: {},
        body: formData,
      });

      if (!email_response.ok) {
        const errorData = await email_response.json().catch(() => null);
        tgaLogEvent('payment_page', { type: 'email_login_error', api: API_ENDPOINTS.REGISTER_ACCOUNT, msg: toString(errorData) });
        throw new Error(errorData?.message || `Login failed with status ${email_response.status}`);
      }

      //根据平台账号登录游戏
      const platformUserData = await email_response.json();
      const platformToken = platformUserData.AccessToken;
      tgaLogEvent('payment_page', { type: 'email_login', msg: JSON.stringify(platformUserData) });

      const loginRequest = {
        Fpid: String(platformUserData.Fpid),
        Sign: platformToken,
        Loginorigin: 6,
        TokenOption: 1,
      };

      window['tga'].login(platformUserData.Fpid);


      // A10 登录 API 调用
      const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.LOGIN_A10), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginRequest),
      });

      if (!response.ok) {
        tgaLogEvent('payment_page', { type: 'email_login_error', api: API_ENDPOINTS.LOGIN_A10, msg: toString(response.status) });
        throw new Error(`Backend returned ${response.status}`);
      }

      const userData = await response.json();
      const todayEnd = extractTodayEndFromLoginResponse(userData);
      const serverNow = extractServerNowFromLoginResponse(userData);
      const serverTimeOffsetMs = serverNow !== null ? Date.now() - (serverNow * 1000) : null;
      const loginGameToken = extractGameTokenFromLoginResponse(userData);


      if (userData && userData.Fpid) {
        const autoLoginUser = {
          id: String(userData.Fpid),
          playerid: userData.Player.ID,
          email: formData.email,
          name: userData.Player?.Name || formData.email.split('@')[0],
          Avatar: userData.Player?.Avatar || '',
          token: platformToken,
          platformToken,
          gameToken: loginGameToken,
          provider: 'email',
          Coin: userData.Player?.Coin || 0,
          Level: userData.Player?.Level || 0,
          VipLevel: userData.Player?.VipLevel || 0,
          Gem: userData.Player?.Gem || 0,
          ...(todayEnd !== null ? { todayEnd, today_end: todayEnd } : {}),
          ...(serverNow !== null ? { serverNow, serverTimeOffsetMs } : {})
        };
        tgaLogEvent('payment_page', { type: 'email_login', msg: JSON.stringify(autoLoginUser) });
        setUser(autoLoginUser);
        return true;
      } else {
        throw new Error('Invalid email login response');
      }
    } catch (error) {
      console.error('Email auto-login failed:', error);
      tgaLogEvent('payment_page', { type: 'email_login_error', msg: toString(error) });
      // 清除无效的凭据
      localStorage.removeItem('emailCredentials');
    }
    return false;
  };

  /**
   * 游戏透传登录后，再用当前 token 去后端拉一次用户信息。
   * 这样顶部展示以服务端为准，避免仅靠 URL 透传字段不完整导致显示为 0。
   */
  const hydrateGameAuthUser = async (baseUser) => {
    try {
      const token = baseUser?.gameToken;
      if (!token || !baseUser?.id || !baseUser?.playerid) {
        return baseUser;
      }

      // 双路由兜底：不同环境/网关可能挂在不同路由下
      const playerInfoEndpoints = [
        API_ENDPOINTS.GET_PLAYER_INFO,
        API_ENDPOINTS.GET_PLAYER_INFO_GATEWAY,
      ].filter(Boolean);

      const requestBodies = [
        // 与游戏端“查玩家信息”用法对齐：显式带 uid/location
        { uid: Number(baseUser.playerid), location: 0 },
        // 兼容部分后端 PascalCase 字段
        { Uid: Number(baseUser.playerid), Location: 0 },
        // 兼容部分后端 playerid 字段命名
        { playerid: Number(baseUser.playerid), location: 0 },
        { Playerid: Number(baseUser.playerid), Location: 0 },
        // 兜底：空 body
        {},
      ];

      for (const endpoint of playerInfoEndpoints) {
        for (const reqBody of requestBodies) {
          const response = await fetchWithRetry(getApiUrl(endpoint), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Gametoken: token,
              Fpid: String(baseUser.id),
              Playerid: String(baseUser.playerid),
            },
            body: JSON.stringify(reqBody),
          }, 1);

          if (!response.ok) {
            console.warn('[AuthChecker] hydrate game-auth user failed, endpoint:', endpoint, 'status:', response.status, 'body:', reqBody);
            continue;
          }

          const data = await response.json().catch(() => null);
          const timePatch = extractCrossDayTimePatch(data);
          const patch = extractUserDisplayPatch(data);
          console.log('[countdown-debug][AuthChecker][hydrate-response]', {
            endpoint,
            body: reqBody,
            extractedTimePatch: timePatch,
            rawEndOfDay: data?.EndOfDay,
            rawNow: data?.Now,
          });

          if (!patch && Object.keys(timePatch).length === 0) {
            console.warn('[AuthChecker] hydrate game-auth user got no player/time patch, endpoint:', endpoint, 'body:', reqBody, 'resp:', data);
            continue;
          }

          return {
            ...baseUser,
            ...(patch || {}),
            ...timePatch,
          };
        }
      }
      return baseUser;
    } catch (error) {
      console.warn('[AuthChecker] hydrate game-auth user failed:', error);
      return baseUser;
    }
  };

  /**
   * 游戏透传登录补齐时间字段：
   * 尝试复用登录接口（与手动登录同源）拿 EndOfDay / Now，
   * 让自动登录和手动登录表现一致。
   */
  const hydrateGameAuthUserByLoginA10 = async (baseUser) => {
    try {
      if (!baseUser?.id || !(baseUser?.platformToken || baseUser?.token)) {
        return baseUser;
      }

      const sign = baseUser?.provider === 'email'
        ? baseUser?.platformToken
        : baseUser?.token;

      const loginRequest = {
        Fpid: String(baseUser.id),
        Sign: sign,
        Loginorigin: 6,
        TokenOption: 1,
      };

      const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.LOGIN_A10), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginRequest),
      }, 1);

      if (!response.ok) {
        console.warn('[AuthChecker] hydrate game-auth by LoginA10 failed:', response.status, loginRequest);
        return baseUser;
      }

      const data = await response.json().catch(() => null);
      const timePatch = extractCrossDayTimePatch(data);
      const userPatch = extractUserDisplayPatch(data);
      const loginGameToken = extractGameTokenFromLoginResponse(data);

      console.log('[countdown-debug][AuthChecker][game-auth][loginA10-response]', {
        request: loginRequest,
        rawEndOfDay: data?.EndOfDay,
        rawNow: data?.Now,
        extractedTimePatch: timePatch,
      });

      if (!userPatch && Object.keys(timePatch).length === 0) {
        return baseUser;
      }

      return {
        ...baseUser,
        ...(userPatch || {}),
        ...(loginGameToken ? { gameToken: loginGameToken } : {}),
        ...timePatch,
      };
    } catch (error) {
      console.warn('[AuthChecker] hydrate game-auth by LoginA10 error:', error);
      return baseUser;
    }
  };

  /**
   * 常规登录态刷新：直接复用 LoginA10（不走 RegisterA10Account）
   */
  const refreshStoredUserByLoginA10 = async (storedUser) => {
    try {
      if (!storedUser?.id) {
        return storedUser;
      }

      const isEmailProvider = storedUser?.provider === 'email';
      const sign = isEmailProvider
        ? storedUser?.platformToken
        : storedUser?.token;
      if (!sign) {
        return storedUser;
      }

      const loginRequest = {
        Fpid: String(storedUser.id),
        Sign: sign,
        Loginorigin: 6,
        TokenOption: 1,
      };

      const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.LOGIN_A10), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginRequest),
      }, 1);

      if (!response.ok) {
        console.warn('[AuthChecker] refresh stored user by LoginA10 failed:', response.status);
        return storedUser;
      }

      const userData = await response.json().catch(() => null);
      if (!userData) {
        return storedUser;
      }

      const todayEnd = extractTodayEndFromLoginResponse(userData);
      const serverNow = extractServerNowFromLoginResponse(userData);
      const serverTimeOffsetMs = serverNow !== null ? Date.now() - (serverNow * 1000) : null;
      const displayPatch = extractUserDisplayPatch(userData) || {};
      const responseTimePatch = extractCrossDayTimePatch(userData);
      const loginGameToken = extractGameTokenFromLoginResponse(userData);

      const refreshedUser = {
        ...storedUser,
        ...displayPatch,
        id: String(userData.Fpid || storedUser.id),
        token: isEmailProvider
          ? (storedUser.platformToken || storedUser.token)
          : storedUser.token,
        gameToken: loginGameToken || storedUser.gameToken,
        ...(todayEnd !== null ? { todayEnd, today_end: todayEnd } : {}),
        ...(serverNow !== null ? { serverNow, serverTimeOffsetMs } : {}),
        ...responseTimePatch,
      };

      console.log('[countdown-debug][AuthChecker][stored-user][loginA10-refresh]', {
        request: loginRequest,
        rawEndOfDay: userData?.EndOfDay,
        rawNow: userData?.Now,
        extractedTimePatch: responseTimePatch,
      });

      return refreshedUser;
    } catch (error) {
      console.warn('[AuthChecker] refresh stored user by LoginA10 error:', error);
      return storedUser;
    }
  };

  useEffect(() => {
    setAuthCheckDone(false);

    const finishCheck = () => {
      setAuthCheckDone(true);
    };

    const checkFacebookLoginStatus = () => {
      // 确保 Facebook SDK 已加载
      if (!window.FB) {
        console.error('Facebook SDK not available');
        // Facebook SDK 不可用时，尝试邮箱自动登录
        checkEmailAutoLogin().finally(() => {
          finishCheck();
        });
        return;
      }

      // 检查 Facebook 登录状态
      FB.getLoginStatus(async (response) => {
        tgaLogEvent('payment_page', { type: 'fb_login', msg: JSON.stringify(response) });

        if (response.status === 'connected') {
          // 用户已登录 Facebook 和应用
          const authResponse = response.authResponse;

          try {
            // 调用后端登录接口
            const loginRequest = {
              appId: '118100871695288',
              openId: authResponse.userID,
              accessToken: authResponse.accessToken,
              signedRequest: authResponse.signedRequest || '',
              tokenOption:1,
            };

            const backendResponse = await fetchWithRetry(getApiUrl(API_ENDPOINTS.LOGIN_FACEBOOK), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(loginRequest),
            });

            if (!backendResponse.ok) {
              tgaLogEvent('payment_page', { type: 'fb_login_error', api: API_ENDPOINTS.LOGIN_FACEBOOK, msg: toString(backendResponse.status) });
              throw new Error(`Backend returned ${backendResponse.status}`);
            }

            const fbUserData = await backendResponse.json();
            const facebookToken = extractGameTokenFromLoginResponse(fbUserData);
            const todayEnd = extractTodayEndFromLoginResponse(fbUserData);
            const serverNow = extractServerNowFromLoginResponse(fbUserData);
            const serverTimeOffsetMs = serverNow !== null ? Date.now() - (serverNow * 1000) : null;

            console.log('[auth-debug][facebook-login][AuthChecker][backend-response]', fbUserData);
            console.log('[auth-debug][facebook-login][AuthChecker][token-candidates]', {
              Token: fbUserData?.Token,
              GameToken: fbUserData?.GameToken,
              Gametoken: fbUserData?.Gametoken,
              gameToken: fbUserData?.gameToken,
            });

            // 设置用户数据
            if (fbUserData && fbUserData.Fpid && facebookToken) {
              window['tga'].login(fbUserData.Fpid);

              console.log('[countdown-debug][AuthChecker][facebook-login]', {
                rawFacebookToken: !!facebookToken,
                todayEnd,
                serverNow,
                rawEndOfDay: fbUserData?.EndOfDay,
                rawNow: fbUserData?.Now,
              });

              const fbUser = {
                id: String(fbUserData.Fpid),
                name: fbUserData.Player?.Name || 'Facebook User',
                Avatar: fbUserData.Player?.Avatar || '',
                token: facebookToken,
                gameToken: facebookToken,
                provider: 'facebook',
                playerid: fbUserData.Player?.ID || fbUserData.PlayerID,
                Coin: fbUserData.Player?.Coin || 0,
                Level: fbUserData.Player?.Level || 0,
                VipLevel: fbUserData.Player?.VipLevel || 0,
                Gem: fbUserData.Player?.Gem || 0,
                ...(todayEnd !== null ? { todayEnd, today_end: todayEnd } : {}),
                ...(serverNow !== null ? { serverNow, serverTimeOffsetMs } : {})
              };
              console.log('[auth-debug][facebook-login][AuthChecker][user-session]', {
                id: fbUser.id,
                playerid: fbUser.playerid,
                hasFacebookToken: !!fbUser.token,
                hasGameToken: !!fbUser.gameToken,
              });
              setUser(fbUser);
              finishCheck();
              tgaLogEvent('payment_page', { type: 'fb_login', msg: JSON.stringify(fbUser) });
            } else {
              // Facebook 登录成功但返回数据无效，尝试邮箱自动登录
              await checkEmailAutoLogin();
              finishCheck();
            }
          } catch (error) {
            console.error('Auto-login failed:', error);
            tgaLogEvent('payment_page', { type: 'fb_login_error', msg: toString(error) });
            // Facebook 自动登录失败，尝试邮箱自动登录
            await checkEmailAutoLogin();
            finishCheck();
          }
        } else if (response.status === 'not_authorized') {
          // 用户已登录 Facebook 但未授权应用
          // Facebook 未授权时，尝试邮箱自动登录
          await checkEmailAutoLogin();
          finishCheck();
        } else {
          // 用户未登录 Facebook
          // Facebook 未登录时，尝试邮箱自动登录
          await checkEmailAutoLogin();
          finishCheck();
        }
      });
    };

    const performAutoLogin = async () => {
      // 使用 useRef 确保只执行一次
      if (hasCheckedRef.current) {
        return;
      }
      hasCheckedRef.current = true;

      // 等待 Facebook SDK 初始化
      const waitForFacebookSDK = () => {
        return new Promise((resolve) => {
          // 检查是否已经初始化（用一个标记）
          if (window.FB && window.fbSDKInitialized) {
            resolve(true);
            return;
          }

          // 监听自定义的 fb-sdk-initialized 事件
          const handler = () => {
            window.fbSDKInitialized = true;
            window.removeEventListener('fb-sdk-initialized', handler);
            resolve(true);
          };
          window.addEventListener('fb-sdk-initialized', handler);

          // 设置超时，避免无限等待
          setTimeout(() => {
            window.removeEventListener('fb-sdk-initialized', handler);
            console.warn('Facebook SDK loading timeout');
            resolve(false);
          }, 10000);
        });
      };

      // 优先处理“游戏内跳转透传登录”
      const gameAuthUser = parseGameAuthUserFromUrl();
      if (gameAuthUser) {
        console.log('[countdown-debug][AuthChecker][game-auth][url-parsed]', gameAuthUser);
        try {
          window['tga']?.login?.(gameAuthUser.id);
        } catch (e) {
          console.warn('tga login failed in game auth flow:', e);
        }

        const hydratedGameAuthUserStep1 = await hydrateGameAuthUser(gameAuthUser);
        const hasTimeAfterStep1 = !!(hydratedGameAuthUserStep1?.todayEnd || hydratedGameAuthUserStep1?.today_end);
        const hydratedGameAuthUser = hasTimeAfterStep1
          ? hydratedGameAuthUserStep1
          : await hydrateGameAuthUserByLoginA10(hydratedGameAuthUserStep1);
        console.log('[countdown-debug][AuthChecker][game-auth][hydrated-user]', {
          parsedTodayEnd: gameAuthUser?.todayEnd ?? gameAuthUser?.today_end,
          hydratedStep1TodayEnd: hydratedGameAuthUserStep1?.todayEnd ?? hydratedGameAuthUserStep1?.today_end,
          hydratedTodayEnd: hydratedGameAuthUser?.todayEnd ?? hydratedGameAuthUser?.today_end,
          hydratedServerNow: hydratedGameAuthUser?.serverNow,
          hydratedServerTimeOffsetMs: hydratedGameAuthUser?.serverTimeOffsetMs,
        });

        // 游戏跳转登录：只保留当前会话，不写 localStorage（刷新后即退出）
        setUser(hydratedGameAuthUser, { persist: false });
        setLoginModalOpen(false);
        sessionStorage.removeItem('sessionExpired');
        sessionStorage.setItem(GAME_AUTH_SESSION_KEY, '1');
        clearGameAuthParamsFromUrl();
        finishCheck();
        return;
      }

      // 如果已经有用户登录：刷新页时也要重校验一次会话
      if (isAuthenticated && user) {
        // 用户是常规登录态时，移除“游戏会话”标记，恢复正常刷新保持登录
        if (user.provider !== 'game') {
          sessionStorage.removeItem(GAME_AUTH_SESSION_KEY);
        }

        if (user.provider === 'facebook') {
          const fbReady = await waitForFacebookSDK();
          if (fbReady) {
            checkFacebookLoginStatus();
          } else {
            setUser(user);
            finishCheck();
          }
          return;
        }

        // 刷新页面时：直接走 LoginA10Request，不再触发 RegisterA10Account
        const refreshedUser = await refreshStoredUserByLoginA10(user);
        setUser(refreshedUser);
        finishCheck();
        return;
      }

      // 游戏跳转登录后，刷新页面应退出登录且不自动重登
      if (sessionStorage.getItem(GAME_AUTH_SESSION_KEY) === '1') {
        setLoginModalOpen(true);
        finishCheck();
        return;
      }

      // 等待 SDK 初始化
      const fbReady = await waitForFacebookSDK();

      if (fbReady) {
        // 先检查 Facebook 登录状态（优先级更高）
        checkFacebookLoginStatus();
      } else {
        // Facebook SDK 不可用，直接尝试邮箱登录
        const emailLoginSuccess = await checkEmailAutoLogin();
        if (emailLoginSuccess) {
          finishCheck();
        } else {
          // 邮箱登录也失败，再次检查Facebook SDK（可能延迟加载）
          setTimeout(() => {
            if (window.FB && window.fbSDKInitialized) {
              checkFacebookLoginStatus();
            } else {
              finishCheck();
            }
          }, 2000);
        }
      }
    };

    // 开始自动登录流程
    performAutoLogin();
  }, []); // 移除所有依赖项，只在组件挂载时执行一次

  // 这个组件不渲染任何内容
  return null;
};

export default AuthChecker;
